import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import * as presentationService from 'features/Presentations/services/presentationService'
import type {
	ElementDescriptor,
	EntityRef,
	Presentation,
	PresentationScope,
	PresentationSummary,
	SavePresentationInput,
} from 'features/Presentations/types/presentation.types'

/**
 * Presentations: the gallery list and the element catalog (MYD-493).
 *
 * Deliberately NOT here: the deck being edited, and resolved block payloads.
 * The editor owns its deck in `useDeckEditor`, where every keystroke would
 * otherwise re-render every store subscriber, and resolved payloads are cached
 * per editor in `useResolvedBlocks`. The same split Explorer makes between its
 * slice and its working definition.
 *
 * What IS here is what a user carries between screens: which decks they have,
 * and which elements exist. Both change rarely.
 */

const SLICE_NAME = 'presentations'

/** The key `elementsByScope` uses for the unscoped (every element) catalog. */
export const ALL_ELEMENTS = 'all'

interface PresentationSliceState {
	list: PresentationSummary[]
	listLoaded: boolean
	/** The element catalog per scope, plus ALL_ELEMENTS. Server-driven and per-user. */
	elementsByScope: Record<string, ElementDescriptor[]>
	isLoading: boolean
	isSaving: boolean
	error: string | null
}

const initialState: PresentationSliceState = {
	list: [],
	listLoaded: false,
	elementsByScope: {},
	isLoading: false,
	isSaving: false,
	error: null,
}

/**
 * The server's message is kept wherever it has one. For a deck it names the
 * offending path (`stories[0].slides[2].blocks[1].region: ...`), which is the
 * only thing that lets the editor point at the block at fault.
 */
const messageOf = (error: any, fallback: string) => error?.response?.data?.message || fallback

export const fetchPresentations = createAsyncThunk(
	`${SLICE_NAME}/fetchPresentations`,
	async (_: void, { rejectWithValue }) => {
		try {
			return await presentationService.listPresentations()
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to load presentations'))
		}
	},
)

export const fetchElements = createAsyncThunk(
	`${SLICE_NAME}/fetchElements`,
	async (scope: PresentationScope | undefined, { rejectWithValue }) => {
		try {
			return { key: scope ?? ALL_ELEMENTS, elements: await presentationService.getElements(scope) }
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to load slide elements'))
		}
	},
)

export const createPresentation = createAsyncThunk(
	`${SLICE_NAME}/createPresentation`,
	async (input: SavePresentationInput, { rejectWithValue }) => {
		try {
			return await presentationService.createPresentation(input)
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to create the presentation'))
		}
	},
)

export const updatePresentation = createAsyncThunk(
	`${SLICE_NAME}/updatePresentation`,
	async ({ id, input }: { id: string; input: SavePresentationInput }, { rejectWithValue }) => {
		try {
			return await presentationService.updatePresentation(id, input)
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to save the presentation'))
		}
	},
)

export const deletePresentation = createAsyncThunk(
	`${SLICE_NAME}/deletePresentation`,
	async (id: string, { rejectWithValue }) => {
		try {
			await presentationService.deletePresentation(id)
			return id
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to delete the presentation'))
		}
	},
)

export const duplicatePresentation = createAsyncThunk(
	`${SLICE_NAME}/duplicatePresentation`,
	async (id: string, { rejectWithValue }) => {
		try {
			return await presentationService.duplicatePresentation(id)
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to duplicate the presentation'))
		}
	},
)

/**
 * A new deck from a deck template (MYD-604). Resolves with the server's
 * warnings and drop count as well as the deck, for the caller to toast.
 */
export const createPresentationFromTemplate = createAsyncThunk(
	`${SLICE_NAME}/createPresentationFromTemplate`,
	async (input: { templateKey: string; anchor: EntityRef; name?: string }, { rejectWithValue }) => {
		try {
			return await presentationService.createPresentationFromTemplate(input)
		} catch (error) {
			return rejectWithValue(messageOf(error, 'Failed to create the presentation'))
		}
	},
)

/** A full deck reduced to the summary the list holds. */
export const toSummary = (presentation: Presentation | PresentationSummary): PresentationSummary => {
	const { definition: _definition, ...summary } = presentation as Presentation
	return summary
}

/**
 * Insert or replace a deck in the list, keeping the server's order: most
 * recently updated first, with an id tiebreak so a duplicate and its source,
 * saved in the same millisecond, do not swap places on every render.
 */
const upsert = (state: PresentationSliceState, presentation: Presentation) => {
	const summary = toSummary(presentation)
	const next = [...state.list.filter(existing => existing.id !== summary.id), summary]
	next.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || b.id.localeCompare(a.id))
	state.list = next
}

const presentationSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearError: state => {
			state.error = null
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchPresentations.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchPresentations.fulfilled, (state, action) => {
				state.list = action.payload
				state.listLoaded = true
				state.isLoading = false
			})
			.addCase(fetchPresentations.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

			.addCase(fetchElements.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchElements.fulfilled, (state, action) => {
				state.elementsByScope[action.payload.key] = action.payload.elements
				state.isLoading = false
			})
			.addCase(fetchElements.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

		for (const thunk of [createPresentation, updatePresentation, duplicatePresentation]) {
			builder
				.addCase(thunk.pending, state => {
					state.isSaving = true
					state.error = null
				})
				.addCase(thunk.fulfilled, (state, action) => {
					upsert(state, action.payload)
					state.isSaving = false
				})
				.addCase(thunk.rejected, (state, action) => {
					state.isSaving = false
					state.error = action.payload as string
				})
		}

		builder
			.addCase(createPresentationFromTemplate.pending, state => {
				state.isSaving = true
				state.error = null
			})
			.addCase(createPresentationFromTemplate.fulfilled, (state, action) => {
				upsert(state, action.payload.presentation)
				state.isSaving = false
			})
			.addCase(createPresentationFromTemplate.rejected, (state, action) => {
				state.isSaving = false
				state.error = action.payload as string
			})

		builder
			.addCase(deletePresentation.fulfilled, (state, action) => {
				state.list = state.list.filter(presentation => presentation.id !== action.payload)
			})
			.addCase(deletePresentation.rejected, (state, action) => {
				state.error = action.payload as string
			})
	},
})

export const { clearError } = presentationSlice.actions

export default presentationSlice
